/** Arpeggiator controls component — UI for configuring the arpeggiator.
 *
 * Provides controls for pattern, rate, octave range, gate length, and latch mode.
 * Styled consistently with other synth effect panels.
 */

import { Repeat, Zap } from "lucide-react";
import { useCallback } from "react";
import type { ArpParams, ArpPattern, ArpRate } from "../lib/Arpeggiator.ts";
import {
	ARP_PATTERN_LABELS,
	ARP_PATTERNS,
	ARP_RATE_LABELS,
	ARP_RATES,
} from "../lib/Arpeggiator.ts";

/** Props for the ArpeggiatorControls component. */
export type ArpeggiatorControlsProps = {
	readonly params: ArpParams;
	readonly onChange: (update: Partial<ArpParams>) => void;
};

export function ArpeggiatorControls({
	params,
	onChange,
}: ArpeggiatorControlsProps) {
	const handlePatternChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			onChange({ pattern: e.target.value as ArpPattern });
		},
		[onChange],
	);

	const handleRateChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			onChange({ rate: e.target.value as ArpRate });
		},
		[onChange],
	);

	const handleOctaveChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onChange({ octaves: Number(e.target.value) });
		},
		[onChange],
	);

	const handleGateChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onChange({ gate: Number(e.target.value) });
		},
		[onChange],
	);

	return (
		<div className="arp-controls">
			<div className="arp-header">
				<div className="arp-title">
					<Zap size={14} />
					<span>Arpeggiator</span>
				</div>
				<div className="arp-toggles">
					<button
						type="button"
						className={`arp-toggle-btn ${params.enabled ? "active" : ""}`}
						onClick={() => onChange({ enabled: !params.enabled })}
						title={
							params.enabled ? "Disable arpeggiator" : "Enable arpeggiator"
						}
					>
						{params.enabled ? "On" : "Off"}
					</button>
					<button
						type="button"
						className={`arp-toggle-btn ${params.latch ? "active" : ""}`}
						onClick={() => onChange({ latch: !params.latch })}
						title={
							params.latch
								? "Disable latch (notes stop when released)"
								: "Enable latch (notes continue after release)"
						}
					>
						<Repeat size={10} />
						<span>Latch</span>
					</button>
				</div>
			</div>

			<div className="arp-body">
				{/* Pattern */}
				<div className="arp-row">
					<label className="arp-label" htmlFor="arp-pattern">
						Pattern
					</label>
					<select
						id="arp-pattern"
						className="arp-select"
						value={params.pattern}
						onChange={handlePatternChange}
					>
						{ARP_PATTERNS.map((p) => (
							<option key={p} value={p}>
								{ARP_PATTERN_LABELS[p]}
							</option>
						))}
					</select>
				</div>

				{/* Rate */}
				<div className="arp-row">
					<label className="arp-label" htmlFor="arp-rate">
						Rate
					</label>
					<select
						id="arp-rate"
						className="arp-select"
						value={params.rate}
						onChange={handleRateChange}
					>
						{ARP_RATES.map((r) => (
							<option key={r} value={r}>
								{ARP_RATE_LABELS[r]}
							</option>
						))}
					</select>
				</div>

				{/* Octaves */}
				<div className="arp-row">
					<label className="arp-label" htmlFor="arp-octaves">
						Octaves
					</label>
					<div className="arp-slider-group">
						<input
							id="arp-octaves"
							type="range"
							className="arp-slider"
							min={1}
							max={4}
							step={1}
							value={params.octaves}
							onChange={handleOctaveChange}
						/>
						<span className="arp-value">{params.octaves}</span>
					</div>
				</div>

				{/* Gate */}
				<div className="arp-row">
					<label className="arp-label" htmlFor="arp-gate">
						Gate
					</label>
					<div className="arp-slider-group">
						<input
							id="arp-gate"
							type="range"
							className="arp-slider"
							min={0.1}
							max={1.0}
							step={0.05}
							value={params.gate}
							onChange={handleGateChange}
						/>
						<span className="arp-value">{Math.round(params.gate * 100)}%</span>
					</div>
				</div>
			</div>
		</div>
	);
}
